import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { DialogContent } from '@material-ui/core';
import store, { adminUserActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { AdminApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react/load-panel';
import { hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import DataGrid, { Column, GroupPanel, Selection, Paging, Pager } from 'devextreme-react/data-grid';

const AdminRolesModal = () => {
    const { isOpen, id } = useSelector((state) => state.adminUsers.adminRolesModal);
    const { dispatch } = store;
    const adminApi = new AdminApi.AdminApi(applicationInsights);

    const [name, set_name] = useState('');
    const [roles, set_roles] = useState([]);
    const [selected, set_selected] = useState([]);

    //Dialogs
    const [loadingMessage, set_loadingMessage] = useState('');

    const allowedPageSizes = [15, 25, 40];
    const defaultPageSize = 25;

    const handleClose = () =>
        dispatch(
            adminUserActions.setAdminRolesModal({
                adminRolesModal: { isOpen: false },
            })
        );

    const styles = {
        headerRight: {
            width: '100px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        buttonContainer: {
            margin: ' 0 20px 0 0',
            textAlign: 'right',
        },
    };

    const getSelected = (role) => {
        return role.granted;
    };

    const getPermissions = async (id) => {
        try {
            set_loadingMessage('Saving...');

            const response = await adminApi.getAdminRoles(id);
            if (response.statusCode !== 200) {
                return;
            }
            const roleData = response.result;
            set_name(roleData.name);
            set_roles(roleData.roles);
            const selectedKeys = roleData.roles.filter(getSelected).map((r) => r.roleTypeId);
            set_selected(selectedKeys);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const saveRoles = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        try {
            set_loadingMessage('Saving...');

            const roleTypeIds = selected;

            set_loadingMessage('Saving changes...');
            const response = await adminApi.updateAdminRoles(id, roleTypeIds);
            if (response.statusCode !== 200) {
                return;
            }
            handleAdminSuccess('User Permissions saved successfully.');

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        resetState();
    }, [isOpen]);

    useEffect(() => {
        if (!id) {
            return;
        }

        getPermissions(id);
    }, [id]);

    const resetState = () => {
        set_name('');
        set_roles([]);
        set_selected([]);
    };

    const uniqueArray = (array, propertyName) => {
        return array.filter((e, i) => array.findIndex((a) => a[propertyName] === e[propertyName]) === i);
    };

    const onRolesSelect = useCallback(
        ({ selectedRowKeys: changedRowKeys, selectedRowsData }) => {
            var alwaysSelected = roles.filter((a) => a.disabled && a.granted);
            const all = [...alwaysSelected, ...selectedRowsData];
            const uniqueRoles = uniqueArray(all, 'roleTypeId');
            set_selected(uniqueRoles.map((a) => a.roleTypeId));
        },
        [roles]
    );

    const onEditorPreparing = (e) => {
        if (e.command == 'select') {
            e.editorOptions.disabled = e?.row?.data?.disabled;
        }
        if (e.parentType == 'headerRow' && e.command == 'select') {
            e.editorElement.remove();
        }
    };

    const onGroupRender = (e) => {
        return <div>{e.value}</div>;
    };

    if (!isOpen || !roles || roles.length === 0) {
        return null;
    }

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen ?? false}
            onClose={handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '900px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <form
                                name='editRolesForm'
                                onSubmit={(event) => saveRoles(event)}
                                className={'standard-form'}
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1
                                        style={{
                                            height: '50px',
                                            marginTop: '15px',
                                        }}
                                    >{`Permissions for ${name}`}</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div
                                    className='scrollable-content'
                                    style={{
                                        padding: '0',
                                        margin: '0',
                                        minHeight: '250px',
                                    }}
                                >
                                    <div style={{ padding: '10px' }}>
                                        <div className='row'>
                                            <div className='columns small-12 large-12'>
                                                <DataGrid
                                                    dataSource={roles}
                                                    keyExpr='roleTypeId'
                                                    showRowLines={true}
                                                    rowAlternationEnabled={true}
                                                    remoteOperations={false}
                                                    allowColumnReordering={true}
                                                    width='100%'
                                                    showBorders={true}
                                                    onSelectionChanged={onRolesSelect}
                                                    selectedRowKeys={selected}
                                                    onEditorPreparing={onEditorPreparing}
                                                    allowSorting={false}
                                                    allowColumnDragging={false}
                                                >
                                                    <Paging defaultPageSize={defaultPageSize} />
                                                    <Pager
                                                        showPageSizeSelector={allowedPageSizes.length > 1}
                                                        allowedPageSizes={allowedPageSizes}
                                                        showNavigationButtons={true}
                                                        showInfo={true}
                                                        visible={true}
                                                    />
                                                    <Selection
                                                        mode='multiple'
                                                        selectAllMode='all'
                                                        showCheckBoxesMode='always'
                                                    />
                                                    <GroupPanel
                                                        visible={true}
                                                        allowColumnDragging={true}
                                                    />
                                                    <Column
                                                        dataField={'categoryName'}
                                                        caption={'Category'}
                                                        groupIndex={0}
                                                        groupCellRender={onGroupRender}
                                                        sortOrder={0}
                                                    />
                                                    <Column
                                                        dataField={'roleName'}
                                                        caption={'Name'}
                                                        width='200'
                                                        allowSorting={false}
                                                    />
                                                    <Column
                                                        dataField={'description'}
                                                        caption={'Role Details'}
                                                        allowSorting={false}
                                                    />
                                                </DataGrid>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='button-footer'>
                                    <div style={styles.buttonContainer}>
                                        <button
                                            type='submit'
                                            className='button primary'
                                            title='Save all changes'
                                            disabled={!hasPermission(PermissionType.PermissionsEdit)}
                                        >
                                            Save
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                            title='Abandon changes and close this window'
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </Dialog>
    );
};

export default AdminRolesModal;
